import React, { useState, useEffect } from 'react';
import { Separator } from "@/components/ui/separator";
import { SidebarTrigger } from "@/components/ui/sidebar";
import { Filter, Calendar, Trash, Clock, X } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import axiosInstance from '@/api/axios';
import { useTranslation } from 'react-i18next';
import CustomCalendar from "@/common/Components/Calendar/Calendar";

// Fallback bin IDs in case the API request fails
const AVAILABLE_BINS = ['R1', 'R2', 'R3', 'R4', 'R5', 'R6', 'R7', 'R9', 'R13', 'R14', 'R15', 'R17', 'IST01', 'IST03', 'B1', 'B2', 'B3'];
const AVAILABLE_YEARS = ['2020', '2021', '2022', '2023', '2024', '2025', '2026'];

// Time periods for dropdown
const TIME_PERIODS = ['day', 'week', 'month', '3months', '6months', 'year'];

// Updated waste types to match Layout component
const WASTE_TYPES = ['organic', 'pmd', 'paper', 'residual'];

export function RecyclingHeader({
  selectedBins,
  setSelectedBins,
  selectedWasteType,
  setSelectedWasteType,
  selectedTimePeriod,
  setSelectedTimePeriod,
  year,
  setYear,
  allBins = [], 
  // Dropdown state control props
  binDropdownOpen: externalBinDropdownOpen,
  setBinDropdownOpen: externalSetBinDropdownOpen,
  yearDropdownOpen: externalYearDropdownOpen,
  setYearDropdownOpen: externalSetYearDropdownOpen,
  timePeriodDropdownOpen: externalTimePeriodDropdownOpen,
  setTimePeriodDropdownOpen: externalSetTimePeriodDropdownOpen,
  wasteTypeDropdownOpen: externalWasteTypeDropdownOpen,
  setWasteTypeDropdownOpen: externalSetWasteTypeDropdownOpen,
  // New props for custom date range
  customDateRange,
  onCustomDateRangeChange,
  isCustomDateRange
}) {
  // Internal dropdown states
  const [internalBinDropdownOpen, setInternalBinDropdownOpen] = useState(false);
  const [internalYearDropdownOpen, setInternalYearDropdownOpen] = useState(false);
  const [internalTimePeriodDropdownOpen, setInternalTimePeriodDropdownOpen] = useState(false);
  const [internalWasteTypeDropdownOpen, setInternalWasteTypeDropdownOpen] = useState(false);
  const [availableBins, setAvailableBins] = useState([]);
  
  // Calendar states
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [localDateRange, setLocalDateRange] = useState(customDateRange || [{
    startDate: null,
    endDate: null,
    key: 'selection'
  }]);
  const [isLocalCustomDateRange, setIsLocalCustomDateRange] = useState(isCustomDateRange || false);

  const { t } = useTranslation();

  // Update local date range when prop changes
  useEffect(() => {
    if (customDateRange) {
      setLocalDateRange(customDateRange);
    }
    if (isCustomDateRange !== undefined) {
      setIsLocalCustomDateRange(isCustomDateRange);
    }
  }, [customDateRange, isCustomDateRange]);

  // Helper function for time range translation
  const getTimeRangeTranslation = (timeRange) => {
    const translations = {
      'day': t('timeRanges.Day'),
      'week': t('timeRanges.Week'),
      'month': t('timeRanges.Month'),
      '3months': t('timeRanges.3Month'),
      '6months': t('timeRanges.6Month'),
      'year': t('timeRanges.Year'),
      'Day': t('timeRanges.Day'),
      'Week': t('timeRanges.Week'),
      'Month': t('timeRanges.Month'),
      '3 Months': t('timeRanges.3Month'),
      '6 Months': t('timeRanges.6Month'),
      'Year': t('timeRanges.Year'),
      'Custom': t('timeRanges.Custom') || 'Custom'
    };
    return translations[timeRange] || timeRange;
  };

  const getWasteTypeTranslation = (wasteType) => {
    const translations = {
      'organic': t('wasteImpact.streams.organic'),
      'pmd': t('wasteImpact.streams.pmd'),
      'paper': t('wasteImpact.streams.paper'),
      'residual': t('wasteImpact.streams.residual'),
    };
    return translations[wasteType] || wasteType;
  };

  const getTimePeriodDisplay = (period) => {
    return getTimeRangeTranslation(period);
  };

  // Fetch bin IDs dynamically
  useEffect(() => {
    const fetchBinIds = async () => {
      try {
        console.log("RecyclingHeader: Fetching bin IDs from API");
        const response = await axiosInstance.get("bin_ids");
        const binIds = response.data || [];
        setAvailableBins(binIds);
      } catch (error) {
        console.error("RecyclingHeader: Error fetching bin IDs:", error);
      }
    };
    fetchBinIds();
  }, []);

  // Determine open/close states (external vs internal)
  const binDropdownOpen = externalBinDropdownOpen !== undefined ? externalBinDropdownOpen : internalBinDropdownOpen;
  const setBinDropdownOpen = externalSetBinDropdownOpen || setInternalBinDropdownOpen;

  const yearDropdownOpen = externalYearDropdownOpen !== undefined ? externalYearDropdownOpen : internalYearDropdownOpen;
  const setYearDropdownOpen = externalSetYearDropdownOpen || setInternalYearDropdownOpen;

  const timePeriodDropdownOpen = externalTimePeriodDropdownOpen !== undefined ? externalTimePeriodDropdownOpen : internalTimePeriodDropdownOpen;
  const setTimePeriodDropdownOpen = externalSetTimePeriodDropdownOpen || setInternalTimePeriodDropdownOpen;

  const wasteTypeDropdownOpen = externalWasteTypeDropdownOpen !== undefined ? externalWasteTypeDropdownOpen : internalWasteTypeDropdownOpen;
  const setWasteTypeDropdownOpen = externalSetWasteTypeDropdownOpen || setInternalWasteTypeDropdownOpen;

  // --- SORTING LOGIC ---
  const rawBinOptions = availableBins.length > 0 ? availableBins : (allBins.length > 0 ? allBins : AVAILABLE_BINS);
  const binOptions = [...rawBinOptions].sort((a, b) => {
    const valA = String(a || '');
    const valB = String(b || '');
    return valA.localeCompare(valB, undefined, { numeric: true, sensitivity: 'base' });
  });

  // Handler functions
  const handleBinChange = (bin) => {
    setSelectedBins(prev =>
      prev.includes(bin)
        ? prev.filter(b => b !== bin)
        : [...prev, bin]
    );
  };

  const handleYearChange = (selectedYear) => {
    setYear(parseInt(selectedYear));
  };

  const handleWasteTypeChange = (wasteType) => {
    setSelectedWasteType(wasteType);
  };

  const handlePeriodChange = (period) => {
    setSelectedTimePeriod(period);
  };

  const handleOpenChange = (open, menuType) => {
    switch (menuType) {
      case 'bin':
        setBinDropdownOpen(open);
        break;
      case 'year':
        setYearDropdownOpen(open);
        break;
      case 'period':
        setTimePeriodDropdownOpen(open);
        break;
      case 'wasteType':
        setWasteTypeDropdownOpen(open);
        break;
    }
  };

  const selectAllBins = () => {
    setSelectedBins([...binOptions]);
  };

  const clearBins = () => {
    setSelectedBins([]);
  };

  // Calendar handlers
  const handleDateChange = (newDateValue) => {
    console.log('RecyclingHeader: Date changed in calendar:', newDateValue);
    setLocalDateRange(newDateValue);
    
    if (newDateValue && newDateValue[0] && newDateValue[0].startDate && newDateValue[0].endDate) {
      setIsLocalCustomDateRange(true);
      setCalendarOpen(false);
      setTimePeriodDropdownOpen(false);
      
      if (onCustomDateRangeChange) {
        console.log('RecyclingHeader: Calling onCustomDateRangeChange with custom dates');
        onCustomDateRangeChange(newDateValue, true);
      }
    }
  };

  const handleResetToTimeRange = () => {
    console.log('RecyclingHeader: Resetting to time range');
    const resetValue = [{
      startDate: null,
      endDate: null,
      key: 'selection'
    }];
    setLocalDateRange(resetValue);
    setIsLocalCustomDateRange(false);
    
    if (onCustomDateRangeChange) {
      console.log('RecyclingHeader: Calling onCustomDateRangeChange with reset');
      onCustomDateRangeChange(resetValue, false);
    }
    
    // Reset to default time period
    if (TIME_PERIODS.length > 0) {
      setSelectedTimePeriod(TIME_PERIODS[0]);
    }
  };

  const getDisplayText = () => {
    if (isLocalCustomDateRange && localDateRange[0]?.startDate && localDateRange[0]?.endDate) {
      const startDate = localDateRange[0].startDate instanceof Date 
        ? localDateRange[0].startDate 
        : new Date(localDateRange[0].startDate);
      const endDate = localDateRange[0].endDate instanceof Date 
        ? localDateRange[0].endDate 
        : new Date(localDateRange[0].endDate);
      
      if (!isNaN(startDate.getTime()) && !isNaN(endDate.getTime())) {
        return `${startDate.toLocaleDateString()} - ${endDate.toLocaleDateString()}`;
      }
    }
    return selectedTimePeriod ? getTimePeriodDisplay(selectedTimePeriod) : t('common.select');
  };

  return (
    <header className="group-has-data-[collapsible=icon]/sidebar-wrapper:h-12 flex h-12 shrink-0 items-center gap-2 transition-[width,height] ease-linear sticky top-0 backdrop-blur-md z-30 border-b w-full max-w-[100vw] overflow-x-hidden px-2 sm:px-4">
      <div className="flex w-full items-center justify-between gap-1">
        <div className="flex items-center min-w-0 max-w-[60%] sm:max-w-none">
          <SidebarTrigger className="-ml-1 h-8 w-8 sm:h-9 sm:w-9" />
          <Separator
            orientation="vertical"
            className="mx-1 sm:mx-2 data-[orientation=vertical]:h-4"
          />
          <h1 className="text-sm sm:text-base font-medium truncate">{t('navigation.circularity')}</h1>
        </div>

        <div className="flex gap-1 sm:gap-2">
          {/* Year Filter */}
          <DropdownMenu
            open={yearDropdownOpen}
            onOpenChange={(open) => handleOpenChange(open, 'year')}
          >
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className="h-8 px-2 text-xs sm:h-9 sm:px-3 sm:text-sm"
              >
                <Calendar className="h-3 w-3 sm:h-4 sm:w-4 opacity-50 mr-1" />
                <span className="hidden sm:inline">{t('header.filters.year')}: </span>
                {year}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              className="w-40"
              onCloseAutoFocus={(e) => e.preventDefault()}
            >
              <DropdownMenuLabel>{t('header.filters.selectYear')}</DropdownMenuLabel>
              <DropdownMenuSeparator />
              {AVAILABLE_YEARS.map(y => (
                <DropdownMenuCheckboxItem
                  key={y}
                  checked={year.toString() === y}
                  onSelect={(e) => e.preventDefault()}
                  onClick={(e) => {
                    e.preventDefault();
                    handleYearChange(y);
                  }}
                >
                  {y}
                </DropdownMenuCheckboxItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Bin Filter */}
          <DropdownMenu
            open={binDropdownOpen}
            onOpenChange={(open) => handleOpenChange(open, 'bin')}
          >
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className="h-8 px-2 text-xs sm:h-9 sm:px-3 sm:text-sm"
              >
                <Trash className="h-3 w-3 sm:h-4 sm:w-4 opacity-50 mr-1" />
                <span className="hidden sm:inline">{t('header.filters.bins')}: </span>
                {selectedBins.length === binOptions.length
                  ? t('common.all')
                  : selectedBins.length === 0
                    ? t('common.clear')
                    : selectedBins.length === 1
                      ? selectedBins[0]
                      : `${selectedBins.length}`}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              className="w-56"
              onCloseAutoFocus={(e) => e.preventDefault()}
            >
              <DropdownMenuLabel className="flex justify-between items-center">
                <span>{t('header.filters.selected')}</span>
                <div className="flex gap-1">
                  <Button
                    variant="ghost"
                    size="xs"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      selectAllBins();
                    }}
                  >
                    {t('common.all')}
                  </Button>
                  <Button
                    variant="ghost"
                    size="xs"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      clearBins();
                    }}
                  >
                    {t('common.clear')}
                  </Button>
                </div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              {binOptions.map(bin => (
                <DropdownMenuCheckboxItem
                  key={bin}
                  checked={selectedBins.includes(bin)}
                  onSelect={(e) => e.preventDefault()}
                  onClick={(e) => {
                    e.preventDefault();
                    handleBinChange(bin);
                  }}
                >
                  {bin}
                </DropdownMenuCheckboxItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Waste Type Filter */}
          <DropdownMenu
            open={wasteTypeDropdownOpen}
            onOpenChange={(open) => handleOpenChange(open, 'wasteType')}
          >
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className="h-8 px-2 text-xs sm:h-9 sm:px-3 sm:text-sm"
              >
                <Filter className="h-3 w-3 sm:h-4 sm:w-4 opacity-50 mr-1" />
                <span className="hidden sm:inline">{t('header.filters.wasteTypes')}: </span>
                {getWasteTypeTranslation(selectedWasteType) || t('common.select')}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              className="w-40"
              onCloseAutoFocus={(e) => e.preventDefault()}
            >
              <DropdownMenuLabel>{t('header.filters.selectWasteType')}</DropdownMenuLabel>
              <DropdownMenuSeparator />
              {WASTE_TYPES.map(wasteType => (
                <DropdownMenuCheckboxItem
                  key={wasteType}
                  checked={selectedWasteType === wasteType}
                  onSelect={(e) => e.preventDefault()}
                  onClick={(e) => {
                    e.preventDefault();
                    handleWasteTypeChange(wasteType);
                  }}
                >
                  {getWasteTypeTranslation(wasteType)}
                </DropdownMenuCheckboxItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Time Period Filter with Calendar */}
          <DropdownMenu
            modal={false}
            open={timePeriodDropdownOpen}
            onOpenChange={(open) => handleOpenChange(open, 'period')}
          >
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className="h-8 px-2 text-xs sm:h-9 sm:px-3 sm:text-sm"
              >
                <Clock className="h-3 w-3 sm:h-4 sm:w-4 opacity-50 mr-1" />
                <span className="hidden sm:inline truncate max-w-[200px]">
                  {t('header.filters.period')}: {getDisplayText()}
                </span>
                <span className="sm:hidden truncate max-w-[100px]">
                  {getDisplayText()}
                </span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              className="w-56"
              onCloseAutoFocus={(e) => e.preventDefault()}
            >
              <div className="flex items-center justify-between px-2 py-1.5">
                <DropdownMenuLabel className="py-0">
                  {t('header.filters.selectTimePeriod')}
                </DropdownMenuLabel>
                {isLocalCustomDateRange && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 px-2 text-xs"
                    onClick={(e) => {
                      e.preventDefault();
                      handleResetToTimeRange();
                    }}
                  >
                    <X className="h-3 w-3 mr-1"/>
                    {t('reset') || 'Reset'}
                  </Button>
                )}
              </div>
              <DropdownMenuSeparator/>
              
              {/* Custom Date Range Option */}
              <DropdownMenuCheckboxItem
                checked={isLocalCustomDateRange}
                onSelect={(e) => {
                  e.preventDefault();
                  setCalendarOpen(true);
                }}
              >
                <Calendar className="h-4 w-4 mr-2"/>
                {t('selectDateRange') || 'Custom Date Range'}
              </DropdownMenuCheckboxItem>
              
              <DropdownMenuSeparator/>
              
              {/* Standard Time Period Options */}
              {TIME_PERIODS.map(period => (
                <DropdownMenuCheckboxItem
                  key={period}
                  checked={!isLocalCustomDateRange && selectedTimePeriod === period}
                  onSelect={(e) => {
                    e.preventDefault();
                    handleResetToTimeRange();
                    handlePeriodChange(period);
                  }}
                >
                  {getTimePeriodDisplay(period)}
                </DropdownMenuCheckboxItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Calendar Dialog */}
          <Dialog open={calendarOpen} onOpenChange={setCalendarOpen}>
            <DialogContent className="sm:max-w-5xl w-full">
              <DialogHeader>
                <DialogTitle>{t('selectDateRange') || 'Select Date Range'}</DialogTitle>
              </DialogHeader>
              <CustomCalendar
                datevalue={localDateRange}
                onDateValueChange={handleDateChange}
              />
            </DialogContent>
          </Dialog>
        </div>
      </div>
    </header>
  );
}